import { VectorRender } from "./Render";
const BEZIER_ARC_C = 0.5522847498307933984022516322796;
/**
 * Rendered arc (circle) on a map.
 * @alias sGis.render.Arc
 */
export class Arc extends VectorRender {
    /**
     * @param center - the center of the arc, in the [x, y] format.
     * @param [options] - key-value options of any Arc parameters
     */
    constructor(center, options = {}) {
        super();
        /** The center of the arc in [x, y] format. */
        this.center = [0, 0];
        /** The radius of the arc. */
        this.radius = 5;
        /** The stroke color of the arc (outline color). The value can be any valid css color string. */
        this.strokeColor = 'black';
        /** The stroke width of the arc. */
        this.strokeWidth = 1;
        /** The fill color of the arc. The value can be any valid css color string. */
        this.fillColor = 'transparent';
        /** Specifies whether this render can catch mouse events. If true, this render will be transparent for any pointer events. */
        this.ignoreEvents = false;
        /** Start angle of the sector. */
        this.startAngle = 0;
        /** End angle of the sector. */
        this.endAngle = 2 * Math.PI;
        /** Shows whether the arc is a sector of a circle rather then simple arc. Set to false if you need to draw a circle, for sector has all its boundaries outlined. */
        this.isSector = false;
        /** Direction of the arc. */
        this.clockwise = true;
        /** @see [[PolyRender.lineDash]] */
        this.lineDash = [];
        /**
         * Property of the Canvas 2D API determines the shape used to draw the end points of lines.
         *
         * - `"butt"`
         *      The ends of lines are squared off at the endpoints.
         * - `"round"`
         *      The ends of lines are rounded.
         * - `"square"`
         *      The ends of lines are squared off by adding a box with an equal width and half the height of the line's thickness.
         */
        this.lineCap = "round";
        Object.assign(this, options);
        this.center = center;
    }
    contains(position) {
        let dx = position[0] - this.center[0];
        let dy = position[1] - this.center[1];
        let distance2 = dx * dx + dy * dy;
        return distance2 < (this.radius + 2) * (this.radius + 2);
    }
    get isVector() { return true; }
    setupRender(ctx, map) {
        ctx.lineWidth = this.strokeWidth;
        ctx.strokeStyle = this.strokeColor;
        ctx.fillStyle = this.fillColor;
        ctx.setLineDash(this.lineDash || []);
        ctx.lineCap = this.lineCap;
    }
    ;
    renderObject(ctx, map) {
        const isCanvas = ctx instanceof CanvasRenderingContext2D;
        // fix for antialias render effect on canvas on high resolution
        if (isCanvas && map && map.getScaleLevel() >= 17) {
            this.renderAsBezierCurves(ctx);
        }
        else {
            this.renderAsArc(ctx);
        }
    }
    ;
    renderAsArc(ctx) {
        const center = this.center;
        const isCanvas = ctx instanceof CanvasRenderingContext2D;
        if (isCanvas) {
            ctx.beginPath();
        }
        if (this.isSector) {
            ctx.moveTo(center[0], center[1]);
        }
        else if (ctx instanceof Path2D) {
            ctx.moveTo(center[0] + this.radius, center[1]);
        }
        ctx.arc(center[0], center[1], this.radius, this.startAngle, this.clockwise ? this.endAngle : -this.endAngle, !this.clockwise);
        if (this.isSector) {
            ctx.lineTo(center[0], center[1]);
        }
        if (isCanvas) {
            ctx.fill();
            ctx.stroke();
        }
    }
    renderAsBezierCurves(ctx) {
        const center = this.center;
        const r = this.radius;
        ctx.translate(center[0], center[1]);
        ctx.beginPath();
        ctx.moveTo(0, -r);
        ctx.bezierCurveTo(BEZIER_ARC_C * r, -r, r, -BEZIER_ARC_C * r, r, 0);
        ctx.fill();
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(r, 0);
        ctx.bezierCurveTo(r, BEZIER_ARC_C * r, BEZIER_ARC_C * r, r, 0, r);
        ctx.fill();
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, r);
        ctx.bezierCurveTo(-BEZIER_ARC_C * r, r, -r, BEZIER_ARC_C * r, -r, 0);
        ctx.fill();
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(-r, 0);
        ctx.bezierCurveTo(-r, -BEZIER_ARC_C * r, -BEZIER_ARC_C * r, -r, 0, -r);
        ctx.fill();
        ctx.stroke();
        if (this.isSector) {
            ctx.beginPath();
            ctx.lineTo(center[0], center[1]);
            ctx.fill();
            ctx.stroke();
        }
    }
}
